import { useEffect } from "react";
import { ensureAbsoluteUrl } from "@/lib/urls";

interface SEOHeadProps {
  title: string;
  description?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown>;
}

export function SEOHead({ title, description, ogImage, ogType = "website", canonicalUrl, jsonLd }: SEOHeadProps) {
  useEffect(() => {
    const fullTitle = `TicketHall — ${title}`;
    document.title = fullTitle;

    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        if (property.startsWith("og:") || property.startsWith("twitter:")) {
          el.setAttribute("property", property);
        } else {
          el.setAttribute("name", property);
        }
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description);
      setMeta("twitter:description", description);
    }
    setMeta("og:title", fullTitle);
    setMeta("twitter:title", fullTitle);
    setMeta("og:type", ogType);
    setMeta("twitter:card", ogImage ? "summary_large_image" : "summary");

    const absoluteOgImage = ogImage ? ensureAbsoluteUrl(ogImage) : "";
    if (absoluteOgImage) {
      setMeta("og:image", absoluteOgImage);
      setMeta("og:image:secure_url", absoluteOgImage);
      setMeta("og:image:type", "image/jpeg");
      setMeta("og:image:width", "1200");
      setMeta("og:image:height", "630");
      setMeta("twitter:image", absoluteOgImage);

      let imageSrc = document.querySelector('link[rel="image_src"]') as HTMLLinkElement;
      if (!imageSrc) {
        imageSrc = document.createElement("link");
        imageSrc.setAttribute("rel", "image_src");
        document.head.appendChild(imageSrc);
      }
      imageSrc.href = absoluteOgImage;
    }

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    // JSON-LD
    const existingLd = document.getElementById("seo-jsonld");
    if (existingLd) existingLd.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = "seo-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const ld = document.getElementById("seo-jsonld");
      if (ld) ld.remove();
    };
  }, [title, description, ogImage, ogType, canonicalUrl, jsonLd]);

  // Injetar script que comunica meta tags para edge function (pre-rendering)
  // Isto permite que serviços como Prerender.io capturem as meta tags
  useEffect(() => {
    const existingScript = document.getElementById("seo-og-bridge");
    if (existingScript) return;

    const script = document.createElement("script");
    script.id = "seo-og-bridge";
    script.type = "application/json";
    script.setAttribute("data-seo-meta", JSON.stringify({
      title,
      description,
      ogImage,
      ogType,
    }));
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [title, description, ogImage, ogType]);

  return null;
}
